import React, { useEffect, useCallback, useRef, RefObject } from 'react';

import { useRouterContext } from 'contexts/router-context';
import useFixedScrollPositionOnPageChange from 'hooks/useFixedScrollPositionOnPageChange';
import useCustomHeaderOnScroll from 'hooks/useCustomHeaderOnScroll';
import useFixedBody from 'hooks/useFixedBody';
import Header from 'components/header';
import GameHeader from 'components/game-header';
import GameInfo from 'components/game-info';
import GameTags from 'components/game-tags';
import MediaCarousel, { Media } from 'components/media-carousel';
import AboutGame from 'components/about-game';
import GameFeatures from 'components/game-features';
import RatingReviews from 'components/rating-reviews';
import GameLikeThis from 'components/games-like-this';
import { trackEvent } from 'utils';
import { TGame } from 'types';

interface IGame {
  game: TGame;
  parentRef: RefObject<any>;
  closeWithAnimation: () => void;
}

const Game: React.FC<IGame> = ({ game, parentRef, closeWithAnimation }) => {
  const { history } = useRouterContext();
  const headerRef = useRef<HTMLDivElement>(null);
  const gameHeaderRef = useRef<HTMLDivElement>(null);

  useFixedBody(true);

  useFixedScrollPositionOnPageChange(parentRef);

  useCustomHeaderOnScroll(parentRef, gameHeaderRef, headerRef);

  useEffect(() => {
    trackEvent(game.trackURL.impression);
  }, [game]);

  const renderHeaderGameInfo = useCallback(() => {
    return (
      <GameHeader game={game} innerRef={gameHeaderRef} parentRef={parentRef} />
    );
  }, [game, parentRef]);

  const media = [
    game.video ? { src: game.video, poster: game.videoPoster } : null,
    ...game.screenshots.map((screenshot) => ({ src: screenshot })),
  ].filter(Boolean) as Media[];

  return (
    <>
      <Header innerRef={headerRef} hideLogo renderChildren={renderHeaderGameInfo} onClick={closeWithAnimation} />
      <GameInfo game={game} />
      {game.tags.length > 0 && <GameTags game={game} />}
      <MediaCarousel media={media} />
      <AboutGame game={game} />
      {game.features.length > 0 && <GameFeatures game={game} />}
      <RatingReviews onlyRating game={game} />
      {history.length <= 5 && <GameLikeThis game={game} />}
    </>
  );
};

export default Game;
